import React, { useState } from 'react';
import { useApp } from '../context/AppContext';

import { getRank } from '../data/initialState';
import { t, getAchievements } from '../data/translations';
import { supabase } from '../supabase';
import { XP_PER_LEVEL, PLAN_LIMITS } from '../data/initialState';

const getCurrency = (lang) => lang==='en'?'$':lang==='da'?'kr (DKK)':lang==='sv'?'kr (SEK)':'kr';
const fmt = (n, lang) => {
  const locale = lang==='en'?'en-US':lang==='sv'?'sv-SE':lang==='da'?'da-DK':'nb-NO';
  const num = Math.round(Math.abs(n)).toLocaleString(locale);
  if (lang==='en') return '$' + num;
  if (lang==='sv') return num + ' kr';
  if (lang==='da') return num + ' kr';
  return num + ' kr';
};

const getPlans = (lang) => [
  { id:'free', name:t(lang,'free'), price:lang==='en'?'Free':lang==='sv'?'0 kr':lang==='da'?'0 kr':'0 kr', color:'#888880', features:[
    lang==='en'?'Daily budget with traffic light':lang==='sv'?'Daglig budget med trafikljus':lang==='da'?'Dagligt budget med trafiklys':'Daglig budsjett med trafikklys',
    lang==='en'?'Savings goals (max 3)':lang==='sv'?'Sparmål (max 3)':lang==='da'?'Sparemål (maks 3)':'Sparemål (maks 3)',
    lang==='en'?'Expense tracking with categories':lang==='sv'?'Utgiftsspårning med kategorier':lang==='da'?'Udgiftssporing med kategorier':'Forbrukssporing med kategorier',
    'XP, levels og streak',
    lang==='en'?'5 achievements to unlock':lang==='sv'?'5 prestationer att låsa upp':lang==='da'?'5 præstationer at låse op':'5 prestasjoner å låse opp',
  ]},
  { id:'advanced', name:'Advanced', price:lang==='en'?'$7.9/month':lang==='sv'?'79 kr/mån':lang==='da'?'79 kr/mdr':'79 kr/mnd', color:'#378ADD', badge:t(lang,'popular'), features:[
    lang==='en'?'Everything in Free':lang==='sv'?'Allt i Gratis':lang==='da'?'Alt i Gratis':'Alt i Gratis',
    lang==='en'?'📊 Savings graph (week/month/year)':lang==='sv'?'📊 Spardiagram (vecka/månad/år)':lang==='da'?'📊 Sparegraf (uge/måned/år)':'📊 Sparegraf (uke/måned/år)',
    lang==='en'?'Unlimited savings goals':lang==='sv'?'Obegränsade sparmål':lang==='da'?'Ubegrænsede sparemål':'Ubegrenset sparemål',
    lang==='en'?'AI-chat with finance coach (20 msg/day)':lang==='sv'?'AI-chatt med ekonomicoach (20 msg/dag)':lang==='da'?'AI-chat med økonomicoach (20 msg/dag)':'AI-chat med økonomicoach (20 meld./dag)',
    lang==='en'?'Detailed spending analysis':lang==='sv'?'Detaljerad utgiftsanalys':lang==='da'?'Detaljeret forbrugsanalyse':'Detaljert forbruksanalyse',
    lang==='en'?'Impulse control — 24-hour rule':lang==='sv'?'Impulskontroll — 24-timmarsregel':lang==='da'?'Impulskontrol — 24-timers regel':'Impulskontroll — 24-timers regel',
    lang==='en'?'Weekly savings reports':lang==='sv'?'Veckovisa sparrapporter':lang==='da'?'Ugentlige sparerapporter':'Ukentlige sparerapporter',
    lang==='en'?'Weekly challenges with XP rewards':lang==='sv'?'Veckovisa utmaningar med XP-belöning':lang==='da'?'Ugentlige udfordringer med XP-belønning':'Ukentlige utfordringer med XP-belønning',
    lang==='en'?'10 exclusive achievements':lang==='sv'?'10 exklusiva prestationer':lang==='da'?'10 eksklusive præstationer':'10 eksklusive prestasjoner',
  ]},
  { id:'premium', name:'Premium', price:lang==='en'?'$14.9/month':lang==='sv'?'149 kr/mån':lang==='da'?'149 kr/mdr':'149 kr/mnd', color:'#7C3AED', badge:t(lang,'best'), features:[
    lang==='en'?'Everything in Advanced':lang==='sv'?'Allt i Advanced':lang==='da'?'Alt i Advanced':'Alt i Advanced',
    lang==='en'?'📊 Savings graph + full history':lang==='sv'?'📊 Spardiagram + full historik':lang==='da'?'📊 Sparegraf + fuld historik':'📊 Sparegraf + full historikk',
    lang==='en'?'Unlimited AI-chat — no daily limit':lang==='sv'?'Obegränsad AI-chatt — ingen daglig gräns':lang==='da'?'Ubegrænset AI-chat — ingen daglig grænse':'Ubegrenset AI-chat — ingen daglig grense',
    lang==='en'?'Subscription tracker — find hidden charges':lang==='sv'?'Prenumerationsspårare — hitta dolda avgifter':lang==='da'?'Abonnementssporing — find skjulte træk':'Abonnement-tracker — finn skjulte trekk',
    lang==='en'?'Predictive finance — "you\'ll run out in X days"':lang==='sv'?'Prediktiv ekonomi — "du får slut om X dagar"':lang==='da'?'Forudsigende økonomi — "du løber tør om X dage"':'Prediktiv økonomi — "du går tom om X dager"',
    lang==='en'?'Behaviour analysis — find your bad habits':lang==='sv'?'Beteendeanalys — hitta dina dåliga vanor':lang==='da'?'Adfærdsanalyse — find dine dårlige vaner':'Atferdsanalyse — finn dine dårlige vaner',
    lang==='en'?'Purchase lock — block impulse buying':lang==='sv'?'Köpslås — blockera impulsköp':lang==='da'?'Købslås — bloker impulskøb':'Kjøpslås — blokker impulsive kjøp',
    lang==='en'?'Weekly personal challenges':lang==='sv'?'Veckovisa personliga utmaningar':lang==='da'?'Ugentlige personlige udfordringer':'Ukentlige personlige utfordringer',
    lang==='en'?'15 prestigious achievements':lang==='sv'?'15 prestigefyllda prestationer':lang==='da'?'15 prestigefyldte præstationer':'15 prestisjefylte prestasjoner',
  ]},
];

const PLAN_BADGE = (lang) => ({ free:`⚪ ${lang==='en'?'Free':lang==='sv'?'Gratis':lang==='da'?'Gratis':'Gratis'}`, advanced:'🔵 Advanced', premium:'🟣 Premium' });

export default function TabProfil({ syncIncome, syncAchievement }) {
  const { state, dispatch } = useApp();
  const lang = state.language || 'en';
  // Sync newly unlocked achievements to Supabase
  React.useEffect(() => {
    if (!state.newlyUnlocked || state.newlyUnlocked.length === 0) return;
    state.newlyUnlocked.forEach(id => {
      if (syncAchievement) syncAchievement(id);
    });
    dispatch({ type: 'CLEAR_NEWLY_UNLOCKED' });
  }, [state.newlyUnlocked]);
  const rank = getRank(state.xp || 0);
  const rankName = rank[lang] || rank.en;
  const [showIncomeEdit, setShowIncomeEdit] = useState(false);
  const [newIncome, setNewIncome] = useState(state.income || 22000);
  const [newExpenses, setNewExpenses] = useState(state.expenses || 0);
  const [newPayDay, setNewPayDay] = useState(String(state.payDay || ''));
  const [achFilter, setAchFilter] = useState('all');
  const [showMoreAch, setShowMoreAch] = useState(false);

  const xpInLevel = state.xp % XP_PER_LEVEL;
  const xpPct = Math.round(xpInLevel / XP_PER_LEVEL * 100);
  const levelNum = Math.min(Math.floor((state.xp || 0) / 500) + 1, 10);
  const levelNames = {
    en: ['',lang==='en'?'Beginner':lang==='sv'?'Nybörjare':lang==='da'?'Begynder':'Nybegynner','Saver','On track','Save Friend','Save Pro','Budget Guru','Master Saver','Money Wisdom','Economy Ninja','Finance Master'],
    no: ['',(lang==='en'?lang==='en'?'Beginner':lang==='sv'?'Nybörjare':lang==='da'?'Begynder':'Nybegynner':lang==='sv'?'Nybörjare':lang==='da'?'Begynder':'Nybegynner'),'Spareforsøk','På vei','Sparevenn','Spareproff','Budsjett-guru','Mestersparer','Pengevisdom','Økonomi-ninja','Finansmester'],
    sv: ['','Nybörjare','Sparare','På rätt spår','Sparvän','Sparse Pro','Budget Guru','Mästarsparare','Pengavisdom','Ekonomi Ninja','Finansmästare'],
    da: ['','Begynder','Opsparer','På vej','Spareven','Spare Pro','Budget Guru','Mestersparer','Pengevisdom','Økonomi Ninja','Finansmester'],
  };
  const levelName = (levelNames[lang] || levelNames.en)[levelNum] || lang==='en'?'Beginner':lang==='sv'?'Nybörjare':lang==='da'?'Begynder':'Nybegynner';
  const saveIncome = () => {
    const inc = parseFloat(newIncome)||0;
    const exp = parseFloat(newExpenses)||0;
    const pd = parseInt(newPayDay);
    const payDayVal = (pd >= 1 && pd <= 31) ? pd : state.payDay;
    dispatch({ type:'UPDATE_INCOME', payload:{ income:inc, expenses:exp, payDay:payDayVal }});
    if (syncIncome) syncIncome(inc, exp, payDayVal);
    setShowIncomeEdit(false);
  };

  const previewIncome = parseFloat(newIncome) || 0;
  const previewExpenses = parseFloat(newExpenses) || 0;
  const now = new Date();
  const daysInMonth = new Date(now.getFullYear(), now.getMonth()+1, 0).getDate();
  const payDayNum = parseInt(newPayDay) || state.payDay || null;
  let daysLeft;
  if (payDayNum && payDayNum >= 1 && payDayNum <= 31) {
    const today = now.getDate();
    if (today <= payDayNum) {
      daysLeft = payDayNum - today;
    } else {
      daysLeft = (daysInMonth - today) + payDayNum;
    }
    daysLeft = Math.max(1, daysLeft);
  } else {
    daysLeft = Math.max(1, daysInMonth - now.getDate() + 1);
  }
  const previewAvailable = Math.max(0, previewIncome - previewExpenses);
  const previewDaily = Math.round(previewAvailable / daysLeft);

  const planOrder = { free:0, advanced:1, premium:2 };
  const userPlanLevel = planOrder[state.plan] || 0;

  const filteredAch = getAchievements(lang).map(a => ({...a, unlocked: (state.achievements || []).find(sa => sa.id === a.id)?.unlocked || false})).filter(a => {
    if (achFilter === 'all') return true;
    if (achFilter === 'unlocked') return a.unlocked;
    return a.plan === achFilter;
  });

  // Visible = plans user has access to
  // Hidden = locked plans shown behind "Vis mer"
  const visiblePlans = state.plan === 'free'
    ? ['free']
    : state.plan === 'advanced'
    ? ['free', 'advanced']
    : ['free', 'advanced', 'premium'];

  const hiddenPlans = state.plan === 'free'
    ? ['advanced', 'premium']
    : state.plan === 'advanced'
    ? ['premium']
    : [];

  // When filter is set to a specific plan, show all from that plan
  const isSpecificFilter = achFilter === 'advanced' || achFilter === 'premium' || achFilter === 'unlocked';

  const shownAch = isSpecificFilter
    ? filteredAch
    : filteredAch.filter(a => visiblePlans.includes(a.plan));

  const hiddenAch = isSpecificFilter
    ? []
    : filteredAch.filter(a => hiddenPlans.includes(a.plan));

  return (
    <div style={{ padding:'1.25rem 1rem' }}>
      {/* User card */}
      <div className="fade-up" style={{ background:'var(--ink)', borderRadius:'var(--r-xl)', padding:'1.5rem', marginBottom:'1rem', position:'relative', overflow:'hidden' }}>
        <div style={{ position:'absolute', top:-7, right:-7, width:120, height:120, borderRadius:'50%', background:'rgba(34,201,122,0.1)' }}/>
        <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:'1.25rem' }}>
          <div style={{ width:58, height:58, borderRadius:'50%', background:'var(--g400)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, color:'var(--ink)', flexShrink:0 }}>
            {state.avatar || state.user?.avatar_emoji || '🌱'}
          </div>
          <div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:800, color:'#fff' }}>{state.username || state.user?.username || state.user?.name}</div>
            <div style={{ fontSize:13, color:'rgba(255,255,255,0.5)' }}>{state.user?.email}</div>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4 }}>
              <div style={{ background:'var(--g400)', color:'var(--ink)', fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:999 }}>{'Lvl ' + levelNum}</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.5)' }}>{levelName}</div>
              <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{PLAN_BADGE(lang)[state.plan]}</div>
            </div>
          </div>
        </div>

        <div style={{ marginBottom:'1rem' }}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, color:'rgba(255,255,255,0.4)', marginBottom:5 }}>
            <span>{xpInLevel} / {XP_PER_LEVEL} XP</span>
            <span>{'Lvl ' + (levelNum+1) + ' →'}</span>
          </div>
          <div style={{ background:'rgba(255,255,255,0.1)', borderRadius:999, height:8, overflow:'hidden' }}>
            <div style={{ width:xpPct+'%', height:'100%', background:'var(--g400)', borderRadius:999 }}/>
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
          {[['🔥', state.streak+(lang==='en'?' days':lang==='sv'?' dagar':lang==='da'?' dage':' dager'),'Streak'],['⚡', state.xp+' XP','Total XP'],['💰', fmt(state.balance),lang==='en'?'Total saved':lang==='sv'?'Totalt sparat':lang==='da'?'Samlet sparet':'Spart totalt']].map(([icon,val,lbl]) => (
            <div key={lbl} style={{ background:'rgba(255,255,255,0.07)', borderRadius:'var(--r-md)', padding:'0.75rem', textAlign:'center' }}>
              <div style={{ fontSize:18, marginBottom:3 }}>{icon}</div>
              <div style={{ fontFamily:'var(--font-display)', fontSize:13, fontWeight:700, color:'#fff' }}>{val}</div>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.35)' }}>{lbl}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Income editor */}
      <div className="fade-up-1" style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1rem', marginBottom:'1rem' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: showIncomeEdit ? '1rem' : 0 }}>
          <div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:14, fontWeight:700, color:'var(--ink)' }}>💳 Økonomi</div>
            {!showIncomeEdit && <div style={{ fontSize:12, color:'var(--ink3)', marginTop:2 }}>Inntekt: {fmt(state.income||0, lang)} · {lang==='en'?'Budget':lang==='sv'?'Budget':lang==='da'?'Budget':'Budsjett'}: {fmt(state.dailyBudget||0, lang)}/dag</div>}
          </div>
          <button onClick={() => setShowIncomeEdit(!showIncomeEdit)} style={{ fontSize:12, color: showIncomeEdit ? 'var(--r400)' : 'var(--g600)', background:'none', border:`1px solid ${showIncomeEdit ? 'var(--r100)' : 'var(--g100)'}`, borderRadius:'var(--r-full)', padding:'5px 12px', fontWeight:600, cursor:'pointer' }}>
            {showIncomeEdit ? lang==='en'?'Cancel':lang==='sv'?'Avbryt':lang==='da'?'Annuller':'Avbryt' : 'Rediger'}
          </button>
        </div>
        {showIncomeEdit && (
          <div className="fade-up">
            <div style={{ marginBottom:10 }}>
              <label style={{ fontSize:12, color:'var(--ink3)', display:'block', marginBottom:4 }}>{lang==='en'?'Monthly income ($)':lang==='sv'?'Månadsinkomst (kr)':lang==='da'?'Månedlig indkomst (kr)':'Månedsinntekt (kr)'}</label>
              <input type="number" value={newIncome} onChange={e=>setNewIncome(e.target.value)} style={{ fontSize:15 }}/>
            </div>
            <div style={{ marginBottom:10 }}>
              <label style={{ fontSize:12, color:'var(--ink3)', display:'block', marginBottom:4 }}>{lang==='en'?'Monthly expenses':lang==='sv'?'Månadsutgifter':lang==='da'?'Månedlige udgifter':'Faste utgifter per måned'}</label>
              <input type="number" value={newExpenses} onChange={e=>setNewExpenses(e.target.value)} style={{ fontSize:15 }}/>
            </div>
            {previewIncome > 0 && (
              <div style={{ background:'var(--g50)', border:'1px solid var(--g200)', borderRadius:'var(--r-md)', padding:'0.75rem', marginBottom:10, fontSize:13, color:'var(--g800)' }}>
                <div>{lang==='en'?'Available: ':lang==='sv'?'Tillgängligt: ':lang==='da'?'Tilgængeligt: ':'Tilgjengelig: '}<strong>{Math.max(0, previewIncome - previewExpenses).toLocaleString()} {lang==='en'?'$/mo':lang==='sv'?'kr/mån':lang==='da'?'kr/mdr':'kr/mnd'}</strong></div>
                <div style={{ marginTop:4 }}>{lang==='en'?'Daily budget: ':lang==='sv'?'Daglig budget: ':lang==='da'?'Dagligt budget: ':'Daglig budsjett: '}<strong>{previewDaily} {lang==='en'?'$/day':lang==='sv'?'kr/dag':lang==='da'?'kr/dag':'kr/dag'}</strong></div>
              </div>
            )}
            <div style={{ fontSize:12, color:'var(--ink3)', marginTop:20, marginBottom:4 }}>
              {lang==='en'?'Payday (day of month)':lang==='sv'?'Lönningsdag':lang==='da'?'Lønningsdag':(lang==='en'?'Payday':lang==='sv'?'Lönedag':lang==='da'?'Lønningsdag':'Lønnsdag')}
            </div>
            <input type="number" min="1" max="31" value={newPayDay} onChange={e => { const v = e.target.value; if (v === '' || (parseInt(v) >= 1 && parseInt(v) <= 31)) setNewPayDay(v); }} 
              placeholder={lang==='en'?'e.g. 25':lang==='sv'?'t.ex. 25':lang==='da'?'f.eks. 25':'f.eks. 25'}
              style={{ fontSize:15, marginBottom:16 }}/>
            <button onClick={saveIncome} style={{ width:'100%', padding:11, background:'var(--g400)', color:'var(--ink)', border:'none', borderRadius:'var(--r-md)', fontFamily:'var(--font-display)', fontSize:14, fontWeight:700, cursor:'pointer' }}>{lang==='en'?'Save ✓':lang==='sv'?'Spara ✓':lang==='da'?'Gem ✓':'Lagre ✓'}</button>
          </div>
        )}
      </div>

      {/* Achievements */}
      <div className="fade-up-2" style={{ marginBottom:'1rem' }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:15, fontWeight:700, color:'var(--ink)', marginBottom:12 }}>
          🏆 {lang==='en'?'Achievements':lang==='sv'?'Prestationer':lang==='da'?'Præstationer':'Prestasjoner'}
        </div>

        {/* Filter tabs */}
        <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap' }}>
          {[
            ['all', lang==='en'?'All':lang==='sv'?'Alla':lang==='da'?'Alle':'Alle'],
            ['unlocked', lang==='en'?'Unlocked':lang==='sv'?'Upplåsta':lang==='da'?'Låst op':'Låst opp'],
            ['advanced', 'Advanced'],
            ['premium', 'Premium'],
          ].map(([v,l]) => (
            <button key={v} onClick={() => setAchFilter(v)} style={{
              fontSize:11, padding:'5px 12px', borderRadius:'var(--r-full)', cursor:'pointer',
              border: achFilter===v ? '2px solid var(--ink)' : '1px solid var(--border)',
              background: achFilter===v ? 'var(--ink)' : 'var(--surface)',
              color: achFilter===v ? 'var(--paper)' : 'var(--ink3)',
              fontWeight: achFilter===v ? 700 : 400,
            }}>{l}</button>
          ))}
        </div>

        {(() => {
          const allAch = getAchievements(lang).map((a, i) => ({
            ...a,
            plan: a.plan || 'free',
            unlocked: (state.achievements || []).some(ua => ua.id === a.id && ua.unlocked === true),
          }));

          const userPlan = state.plan || 'free';
          const isPro2 = userPlan === 'advanced' || userPlan === 'premium';
          const isPremium2 = userPlan === 'premium';

          let visible = [];
          let hidden = [];

          if (achFilter === 'unlocked') {
            // Only show achievements the user CAN have AND has unlocked
            const planAch = isPremium2 ? allAch : isPro2 ? allAch.filter(a => a.plan !== 'premium') : allAch.filter(a => a.plan === 'free');
            visible = planAch.filter(a => a.unlocked);
          } else if (achFilter === 'advanced') {
            visible = allAch.filter(a => a.plan === 'advanced');
            if (!isPro2) hidden = visible, visible = [];
          } else if (achFilter === 'premium') {
            visible = allAch.filter(a => a.plan === 'premium');
            if (!isPremium2) hidden = visible, visible = [];
          } else {
            // 'all' - show based on plan with locked ones hidden behind button
            if (isPremium2) {
              visible = allAch;
            } else if (isPro2) {
              visible = allAch.filter(a => a.plan !== 'premium');
              hidden = allAch.filter(a => a.plan === 'premium');
            } else {
              visible = allAch.filter(a => a.plan === 'free');
              hidden = allAch.filter(a => a.plan !== 'free');
            }
          }

          const AchCard = ({ a, locked }) => (
            <div style={{
              background: a.unlocked && !locked
                ? 'linear-gradient(135deg, rgba(34,201,122,0.12), rgba(34,201,122,0.04))'
                : 'var(--card)',
              border: `1.5px solid ${a.unlocked && !locked ? '#22C97A' : locked ? 'var(--border)' : 'var(--border)'}`,
              borderRadius:'var(--r-md)', padding:'10px 8px', textAlign:'center',
              position:'relative', overflow:'hidden',
              opacity: locked ? 0.55 : 1,
            }}>
              {/* Fullført — grønn hake BARE hvis virkelig unlocked */}
              {a.unlocked === true && !locked && (
                <div style={{ position:'absolute', top:4, right:4, width:16, height:16, borderRadius:'50%', background:'#22C97A', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, color:'#fff', fontWeight:800 }}>✓</div>
              )}
              {/* Ikke gjort — ingen hake, ingenting */}
              {/* Låst plan — liten lås */}
              {locked && (
                <div style={{ position:'absolute', top:4, right:4, fontSize:11 }}>🔒</div>
              )}
              <div style={{ fontSize:26, marginBottom:4 }}>{locked ? '🔒' : a.emoji}</div>
              <div style={{ fontSize:11, fontWeight:700, color: a.unlocked && !locked ? 'var(--g600)' : locked ? 'var(--ink3)' : 'var(--ink)' }}>
                {a.title}
              </div>
              <div style={{ fontSize:9, color: locked ? 'var(--ink4)' : 'var(--ink3)', marginTop:2 }}>
                {locked ? (lang==='en'?'🔒 Upgrade to unlock':lang==='sv'?'🔒 Uppgradera':lang==='da'?'🔒 Opgrader':'🔒 Oppgrader') : a.desc}
              </div>
              <div style={{ fontSize:9, fontWeight:700, marginTop:4, color: a.unlocked && !locked ? 'var(--g400)' : 'var(--ink4)' }}>
                {a.unlocked && !locked ? `+${a.xp} XP ✓` : `${a.xp} XP`}
              </div>
            </div>
          );

          return (
            <div>
              {visible.length === 0 ? (
                <div style={{ textAlign:'center', padding:'1.5rem', color:'var(--ink3)', fontSize:13 }}>
                  {lang==='en'?'No achievements here yet — keep saving! 💪':lang==='sv'?'Inga prestationer ännu — fortsätt spara! 💪':lang==='da'?'Ingen præstationer endnu — bliv ved med at spare! 💪':'Ingen prestasjoner ennå — fortsett å spare! 💪'}
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
                  {visible.map(a => <AchCard key={a.id} a={a} locked={false}/>)}
                </div>
              )}

              {hidden.length > 0 && (
                <div style={{ marginTop:12 }}>
                  {!showMoreAch ? (
                    <button onClick={() => setShowMoreAch(true)} style={{
                      width:'100%', padding:'10px', borderRadius:'var(--r-md)',
                      border:'1px dashed var(--border)', background:'var(--surface)',
                      color:'var(--ink3)', fontSize:12, cursor:'pointer',
                    }}>
                      🔒 {lang==='en'?`${hidden.length} more achievements — upgrade to unlock`:lang==='sv'?`${hidden.length} fler prestationer — uppgradera för att låsa upp`:lang==='da'?`${hidden.length} flere præstationer — opgrader for at låse op`:`${hidden.length} flere prestasjoner — oppgrader for å låse opp`}
                    </button>
                  ) : (
                    <div>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginTop:8 }}>
                        {hidden.map(a => <AchCard key={a.id} a={a} locked={true}/>)}
                      </div>
                      <button onClick={() => setShowMoreAch(false)} style={{ marginTop:8, fontSize:11, color:'var(--ink3)', background:'none', border:'none', cursor:'pointer', width:'100%' }}>
                        {lang==='en'?'Show less':lang==='sv'?'Visa mindre':lang==='da'?'Vis mindre':'Vis mindre'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}
      </div>


      {/* Abonnement */}
      <div className="fade-up-2" style={{ marginBottom:'1rem' }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:15, fontWeight:700, color:'var(--ink)', marginBottom:12 }}>
          💎 {lang==='en'?'Subscription':lang==='sv'?'Prenumeration':lang==='da'?'Abonnement':'Abonnement'}
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {[
            { id:'free', name:lang==='en'?'Free':lang==='sv'?'Gratis':lang==='da'?'Gratis':'Gratis', price:lang==='en'?'Free':lang==='sv'?'0 kr':lang==='da'?'0 kr':'0 kr', color:'#888880', features:[
              lang==='en'?'✓ Daily budget with traffic light':lang==='sv'?'✓ Daglig budget med trafikljus':lang==='da'?'✓ Dagligt budget med trafiklys':'✓ Daglig budsjett med trafikklys',
              lang==='en'?'✓ Savings goals (max 3)':lang==='sv'?'✓ Sparmål (max 3)':lang==='da'?'✓ Sparemål (maks 3)':'✓ Sparemål (maks 3)',
              lang==='en'?'✓ Expense tracking with categories':lang==='sv'?'✓ Utgiftsspårning med kategorier':lang==='da'?'✓ Udgiftssporing med kategorier':'✓ Forbrukssporing med kategorier',
              lang==='en'?'✓ XP, levels and streak':lang==='sv'?'✓ XP, nivåer och streak':lang==='da'?'✓ XP, niveauer og streak':'✓ XP, nivåer og streak',
              lang==='en'?'✓ Weekly savings graph':lang==='sv'?'✓ Veckografen':lang==='da'?'✓ Ugegraf':'✓ Ukentlig sparegraf',
              lang==='en'?'✓ 5 achievements':lang==='sv'?'✓ 5 prestationer':lang==='da'?'✓ 5 præstationer':'✓ 5 prestasjoner',
            ] },
            { id:'advanced', name:'Advanced', price:lang==='en'?'$7.9/month':lang==='sv'?'79 kr/mån':lang==='da'?'79 kr/mdr':'79 kr/mnd', color:'#378ADD', badge:lang==='en'?'Popular':lang==='sv'?'Populär':lang==='da'?'Populær':'Populær', features:[
              lang==='en'?'✓ Everything in Free':lang==='sv'?'✓ Allt i Gratis':lang==='da'?'✓ Alt i Gratis':'✓ Alt i Gratis',
              lang==='en'?'✓ Unlimited savings goals':lang==='sv'?'✓ Obegränsade sparmål':lang==='da'?'✓ Ubegrænsede sparemål':'✓ Ubegrenset sparemål',
              lang==='en'?'✓ Savings graph (week/month/year)':lang==='sv'?'✓ Spardiagram':lang==='da'?'✓ Sparegraf':'✓ Sparegraf (uke/mnd/år)',
              lang==='en'?'✓ AI Finance Coach (20 msg/day)':lang==='sv'?'✓ AI-ekonomicoach (20/dag)':lang==='da'?'✓ AI-finanscoach (20/dag)':'✓ AI-økonomicoach (20 mld/dag)',
              lang==='en'?'✓ Weekly savings report':lang==='sv'?'✓ Veckorapport':lang==='da'?'✓ Ugentlig rapport':'✓ Ukentlig sparerapport',
              lang==='en'?'✓ Weekly challenges + XP':lang==='sv'?'✓ Veckoutmaningar + XP':lang==='da'?'✓ Ugentlige udfordringer + XP':'✓ Ukentlige utfordringer + XP',
              lang==='en'?'✓ Family group':lang==='sv'?'✓ Familjegrupp':lang==='da'?'✓ Familiegruppe':'✓ Familiegruppe',
              lang==='en'?'✓ 10 exclusive achievements':lang==='sv'?'✓ 10 exklusiva prestationer':lang==='da'?'✓ 10 eksklusive præstationer':'✓ 10 eksklusive prestasjoner',
            ] },
            { id:'premium', name:'Premium', price:lang==='en'?'$14.9/month':lang==='sv'?'149 kr/mån':lang==='da'?'149 kr/mdr':'149 kr/mnd', color:'#7C3AED', badge:lang==='en'?'Best':lang==='sv'?'Bäst':lang==='da'?'Bedst':'Best', features:[
              lang==='en'?'✓ Everything in Advanced':lang==='sv'?'✓ Allt i Advanced':lang==='da'?'✓ Alt i Advanced':'✓ Alt i Advanced',
              lang==='en'?'✓ Unlimited AI-chat — no daily limit':lang==='sv'?'✓ Obegränsad AI-chatt':lang==='da'?'✓ Ubegrænset AI-chat':'✓ Ubegrenset AI-chat — ingen daglig grense',
              lang==='en'?'✓ Weekly personal challenges':lang==='sv'?'✓ Veckovisa personliga utmaningar':lang==='da'?'✓ Ugentlige personlige udfordringer':'✓ Ukentlige personlige utfordringer',
              lang==='en'?'✓ 3 exclusive premium themes':lang==='sv'?'✓ 3 exklusiva teman':lang==='da'?'✓ 3 eksklusive temaer':'✓ 3 eksklusive premium-temaer',
              lang==='en'?'✓ Subscription tracker — find hidden charges':lang==='sv'?'✓ Prenumerationsspårare':lang==='da'?'✓ Abonnementssporing':'✓ Abonnement-tracker — finn skjulte trekk',
              lang==='en'?'✓ Behaviour analysis — find bad spending habits':lang==='sv'?'✓ Beteendeanalys':lang==='da'?'✓ Adfærdsanalyse':'✓ Atferdsanalyse — finn dårlige vaner',
              lang==='en'?'✓ Predictive budget — "runs out in X days"':lang==='sv'?'✓ Prediktiv budget':lang==='da'?'✓ Forudsigende budget':'✓ Prediktivt budsjett — "går tom om X dager"',
              lang==='en'?'✓ Purchase lock — block impulse buying':lang==='sv'?'✓ Köpslås':lang==='da'?'✓ Købslås':'✓ Kjøpslås — blokker impulsive kjøp',
              lang==='en'?'✓ Priority support':lang==='sv'?'✓ Prioriterad support':lang==='da'?'✓ Prioriteret support':'✓ Prioritert support',
              lang==='en'?'✓ 15 prestigious achievements':lang==='sv'?'✓ 15 prestigefyllda prestationer':lang==='da'?'✓ 15 prestigefyldte præstationer':'✓ 15 prestisjefylte prestasjoner',
            ] },
          ].map(plan => (
            <div key={plan.id} style={{
              background: state.plan===plan.id ? `${plan.color}15` : 'var(--surface)',
              border: `2px solid ${state.plan===plan.id ? plan.color : 'var(--border)'}`,
              borderRadius:'var(--r-lg)', padding:'14px 16px',
            }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, position:'relative' }}>
                  <div style={{ fontFamily:'var(--font-display)', fontSize:15, fontWeight:700, color:plan.color }}>{plan.name}{plan.id==='premium' && <span style={{ fontSize:9, position:'absolute', top:-7, right:-7 }}>👑</span>}</div>
                </div>
                  {plan.badge && <div style={{ fontSize:9, fontWeight:700, background:plan.color, color:'#fff', borderRadius:999, padding:'2px 8px' }}>{plan.badge}</div>}
                  {state.plan===plan.id && <div style={{ fontSize:9, fontWeight:700, background:'var(--g400)', color:'var(--ink)', borderRadius:999, padding:'2px 8px' }}>{lang==='en'?'Current':lang==='sv'?'Nuvarande':lang==='da'?'Nuværende':'Nåværende'}</div>}
                </div>
                <div style={{ fontSize:13, fontWeight:700, color:'var(--ink2)' }}>{plan.price}</div>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:3, marginBottom: state.plan!==plan.id ? 10 : 0 }}>
                {plan.features.map((f,i) => <div key={i} style={{ fontSize:11, color:'var(--ink3)', display:'flex', gap:4 }}>{f}</div>)}
              </div>
              {state.plan!==plan.id && (
                <button onClick={() => {
                  // TODO: Stripe payment
                  alert(lang==='en'?'Payment coming soon! Contact us at support@mysavely.app':lang==='sv'?'Betalning kommer snart! Kontakta oss på support@mysavely.app':lang==='da'?'Betaling kommer snart! Kontakt os på support@mysavely.app':'Betaling kommer snart! Kontakt oss på support@mysavely.app');
                }} style={{
                  width:'100%', padding:'10px', borderRadius:'var(--r-md)', border:'none',
                  background: plan.color, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer',
                }}>
                  {lang==='en'?`Upgrade to ${plan.name}`:lang==='sv'?`Uppgradera till ${plan.name}`:lang==='da'?`Opgrader til ${plan.name}`:`Oppgrader til ${plan.name}`} →
                </button>
              )}
            </div>
          ))}
        </div>
      </div>


      {/* Innstillinger */}
      <div className="fade-up-2" style={{ background:'var(--card)', border:'1.5px solid var(--border)', borderRadius:'var(--r-lg)', padding:'1.25rem', marginBottom:'1rem' }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:15, fontWeight:700, color:'var(--ink)', marginBottom:16 }}>
          ⚙️ {lang==='en'?'Settings':lang==='sv'?'Inställningar':lang==='da'?'Indstillinger':'Innstillinger'}
        </div>

  
        {/* Tema */}
        <div style={{ marginBottom:20 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--ink3)', marginBottom:10 }}>
            {lang==='en'?'Theme':lang==='sv'?'Tema':lang==='da'?'Tema':'Tema'}
          </div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {[
              { id:'sapphire', label:'Sapphire Drift',  c1:'#2C5DA9', c2:'#0A0F3C', c3:'#C8DAF9' },
              { id:'velvet',   label:'Velvet Sunset',   c1:'#AF5279', c2:'#3F1D38', c3:'#FFD6A5' },
              { id:'lilac',    label:'Burnt Lilac Mist',c1:'#6F4E7C', c2:'#2C1B28', c3:'#D6C5DC' },
              { id:'garnet',   label:'Garnet Veil',     c1:'#7A2D35', c2:'#2B0C0E', c3:'#C46565' },
              { id:'golden',   label:'Golden Bloom',    c1:'#C49A6C', c2:'#4D3F29', c3:'#FFF3D1' },
              { id:'moondust', label:'Iced Moondust',   c1:'#787A84', c2:'#383A44', c3:'#DFE0E2' },
              { id:'neon',     label:'Neon',            c1:'#00FF88', c2:'#003322', c3:'#00B4D8', premium:true },
              { id:'aurora',   label:'Aurora',          c1:'#A78BFA', c2:'#1a0533', c3:'#34D399', premium:true },
              { id:'obsidian', label:'Obsidian',        c1:'#374151', c2:'#111827', c3:'#6B7280', premium:true },
            ].map(t => (
              <div key={t.id} style={{ textAlign:'center' }}>
                <button onClick={() => {
                  if (t.premium && state.plan !== 'premium') return;
                  dispatch({ type:'SET_THEME', payload: t.id });
                  const all = ['sapphire','velvet','lilac','garnet','golden','moondust','neon','aurora','obsidian'];
                  all.forEach(th => document.body.classList.remove('theme-'+th));
                  document.body.classList.add('theme-'+t.id);
                }} title={t.label} style={{
                  width: state.theme===t.id ? 52 : 44,
                  height: state.theme===t.id ? 52 : 44,
                  borderRadius:'50%',
                  border: state.theme===t.id ? '3px solid rgba(255,255,255,0.9)' : '2px solid rgba(255,255,255,0.15)',
                  cursor: t.premium && state.plan !== 'premium' ? 'not-allowed' : 'pointer',
                  transition:'all 0.25s', flexShrink:0,
                  boxShadow: state.theme===t.id ? `0 0 20px ${t.c1}80, 0 0 40px ${t.c1}30` : '0 2px 8px rgba(0,0,0,0.3)',
                  overflow:'hidden', position:'relative', background:'transparent',
                }}>
                  <div style={{ position:'absolute', inset:0, background:t.c1, clipPath:'polygon(0 0, 50% 0, 50% 100%, 0 100%)' }}/>
                  <div style={{ position:'absolute', inset:0, background:t.c3||t.c2, clipPath:'polygon(50% 0, 100% 0, 100% 100%, 50% 100%)' }}/>
                  {state.theme===t.id && (
                    <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <div style={{ width:8, height:8, borderRadius:'50%', background:'rgba(255,255,255,0.9)' }}/>
                    </div>
                  )}
                  {t.premium && state.plan !== 'premium' && (
                    <div style={{ position:'absolute', top:-7, right:-7, fontSize:8, background:'#7C3AED', color:'#fff', borderRadius:999, padding:'2px 6px', fontWeight:900, letterSpacing:0.5, lineHeight:1.4, zIndex:3, boxShadow:'0 2px 6px rgba(124,58,237,0.5)' }}>PRO</div>
                  )}
                </button>
                <div style={{ fontSize:8, color:'var(--ink4)', marginTop:3, maxWidth:52, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', textAlign:'center' }}>{t.label}</div>



              </div>
            ))}
          </div>
        </div>

        {/* Dark mode */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:14, borderTop:'1px solid var(--border)', marginBottom:14 }}>
          <div>
            <div style={{ fontSize:13, fontWeight:600, color:'var(--ink)' }}>
              {lang==='en'?'🌙 Dark mode':lang==='sv'?'🌙 Mörkt läge':lang==='da'?'🌙 Mørk tilstand':'🌙 Mørk modus'}
            </div>
  
          </div>
          <button onClick={() => dispatch({ type:'TOGGLE_DARK' })} style={{
            width:36, height:20, borderRadius:999, border:'none', cursor:'pointer', padding:0,
            background: state.darkMode ? '#22C97A' : '#CBD5E1',
            position:'relative', transition:'background 0.3s', flexShrink:0,
          }}>
            <div style={{
              width:14, height:14, borderRadius:'50%', background:'#fff',
              position:'absolute', top:3, transition:'left 0.25s',
              left: state.darkMode ? 19 : 3, boxShadow:'0 1px 3px rgba(0,0,0,0.25)',
            }}/>
          </button>
        </div>

        {/* Språk */}
        <div style={{ paddingTop:14, borderTop:'1px solid var(--border)', marginBottom:14 }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--ink3)', marginBottom:10 }}>
            {lang==='en'?'Language':lang==='sv'?'Språk':lang==='da'?'Sprog':'Språk'}
          </div>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
            {[['no','🇳🇴 Norsk'],['en','🇬🇧 English'],['sv','🇸🇪 Svenska'],['da','🇩🇰 Dansk']].map(([code,label]) => (
              <button key={code} onClick={() => {
                dispatch({ type:'SET_LANGUAGE', payload: code });
                try { localStorage.setItem('savely_lang', code); } catch(e) {}
              }} style={{
                padding:'6px 12px', borderRadius:'var(--r-full)', fontSize:12, fontWeight:600,
                border: lang===code ? '2px solid var(--g400)' : '1px solid var(--border)',
                background: lang===code ? 'var(--g50)' : 'var(--surface)',
                color: lang===code ? 'var(--g600)' : 'var(--ink3)', cursor:'pointer',
              }}>{label}</button>
            ))}
          </div>
        </div>

        {/* Kontosikkerhet */}
        <div style={{ paddingTop:14, borderTop:'1px solid var(--border)' }}>
          <div style={{ fontSize:12, fontWeight:600, color:'var(--ink3)', marginBottom:10 }}>
            {lang==='en'?'Account security':lang==='sv'?'Kontosäkerhet':lang==='da'?'Kontosikkerhed':'Kontosikkerhet'}
          </div>
          <button onClick={async () => {
            const email = state.user?.email;
            if (!email) return;
            try {
              await supabase.auth.resetPasswordForEmail(email, { redirectTo:'https://mysavely.app' });
              alert(lang==='en'?'Password reset email sent!':lang==='sv'?'Lösenordsåterställning skickad!':lang==='da'?'Nulstilling af adgangskode sendt!':'E-post for tilbakestilling av passord sendt!');
            } catch(e) { alert('Error: ' + e.message); }
          }} style={{ width:'100%', padding:'10px', borderRadius:'var(--r-md)', border:'1px solid var(--border)', background:'var(--surface)', color:'var(--ink2)', fontSize:13, cursor:'pointer', marginBottom:8, textAlign:'left' }}>
            🔐 {lang==='en'?'Change password':lang==='sv'?'Byt lösenord':lang==='da'?'Skift adgangskode':'Endre passord'}
          </button>
        </div>
      </div>

      {/* Slett konto */}
      <button onClick={async () => {
        const confirmed = window.confirm(
          lang==='en'?'Are you sure you want to delete your account? This cannot be undone.':
          lang==='sv'?'Är du säker på att du vill radera ditt konto? Detta kan inte ångras.':
          lang==='da'?'Er du sikker på at du vil slette din konto? Dette kan ikke fortrydes.':
          lang==='en'?'Are you sure you want to delete your account? This cannot be undone.':lang==='sv'?'Är du säker på att du vill radera ditt konto? Detta kan inte ångras.':lang==='da'?'Er du sikker på at du vil slette din konto? Dette kan ikke fortrydes.':'Er du sikker på at du vil slette kontoen din? Dette kan ikke angres.'
        );
        if (!confirmed) return;
        try {
          if (state.user?.id) {
            await supabase.from('transactions').delete().eq('user_id', state.user.id);
            await supabase.from('goals').delete().eq('user_id', state.user.id);
            await supabase.from('profiles').delete().eq('id', state.user.id);
          }
          await supabase.auth.signOut();
          dispatch({ type:'LOGOUT' });
          alert(lang==='en'?'Your account has been deleted.':lang==='sv'?'Ditt konto har raderats.':lang==='da'?'Din konto er blevet slettet.':'Kontoen din er slettet.');
        } catch(e) {
          alert(lang==='en'?'Something went wrong. Please contact support@mysavely.app':lang==='sv'?'Något gick fel. Kontakta support@mysavely.app':lang==='da'?'Noget gik galt. Kontakt support@mysavely.app':'Noe gikk galt. Kontakt support@mysavely.app');
        }
      }} style={{ width:'100%', padding:'13px', borderRadius:12, border:'1px solid rgba(255,100,100,0.3)', background:'rgba(255,50,50,0.08)', color:'#ff6b6b', fontSize:14, fontWeight:600, cursor:'pointer', marginTop:8 }}>
        {lang==='en'?'🗑️ Delete account':lang==='sv'?'🗑️ Radera konto':lang==='da'?'🗑️ Slet konto':'🗑️ Slett konto'}
      </button>
      <div style={{ height:20 }}/>
    </div>
  );
}
